import api from './api';

export interface Sale {
  id: number;
  customerName: string;
  customerEmail: string;
  products: {
    id: number;
    name: string;
    quantity: number;
    price: number;
  }[];
  total: number;
  status: 'pending' | 'completed' | 'cancelled';
  date: string;
  paymentMethod: string;
}

export interface SaleFormData {
  customerName: string;
  customerEmail: string;
  products: {
    productId: number;
    quantity: number;
    price: number;
  }[];
  paymentMethod: string;
}

// Mock data para demonstração
const mockSales: Sale[] = [
  {
    id: 1,
    customerName: 'João Silva',
    customerEmail: 'joao@email.com',
    products: [
      { id: 1, name: 'Produto A', quantity: 2, price: 50.00 },
      { id: 2, name: 'Produto B', quantity: 1, price: 30.00 }
    ],
    total: 130.00,
    status: 'completed',
    date: '2024-01-15T10:30:00Z',
    paymentMethod: 'credit_card'
  },
  {
    id: 2,
    customerName: 'Maria Santos',
    customerEmail: 'maria@email.com',
    products: [
      { id: 3, name: 'Produto C', quantity: 1, price: 75.00 }
    ],
    total: 75.00,
    status: 'pending',
    date: '2024-01-16T14:20:00Z',
    paymentMethod: 'pix'
  },
  {
    id: 3,
    customerName: 'Pedro Oliveira',
    customerEmail: 'pedro@email.com',
    products: [
      { id: 1, name: 'Produto A', quantity: 3, price: 50.00 },
      { id: 4, name: 'Produto D', quantity: 2, price: 25.00 }
    ],
    total: 200.00,
    status: 'completed',
    date: '2024-01-17T09:15:00Z',
    paymentMethod: 'debit_card'
  },
  {
    id: 4,
    customerName: 'Ana Costa',
    customerEmail: 'ana@email.com',
    products: [
      { id: 2, name: 'Produto B', quantity: 4, price: 30.00 }
    ],
    total: 120.00,
    status: 'cancelled',
    date: '2024-01-18T16:45:00Z',
    paymentMethod: 'cash'
  },
  {
    id: 5,
    customerName: 'Carlos Ferreira',
    customerEmail: 'carlos@email.com',
    products: [
      { id: 5, name: 'Produto E', quantity: 1, price: 150.00 },
      { id: 1, name: 'Produto A', quantity: 1, price: 50.00 }
    ],
    total: 200.00,
    status: 'pending',
    date: '2024-01-19T11:30:00Z',
    paymentMethod: 'bank_transfer'
  }
];

let salesData = [...mockSales];
let nextId = 6;

export const salesService = {
  async getAll(): Promise<Sale[]> {
    // Simula delay da API
    await new Promise(resolve => setTimeout(resolve, 500));
    return salesData;
  },

  async getById(id: number): Promise<Sale | null> {
    await new Promise(resolve => setTimeout(resolve, 300));
    return salesData.find(sale => sale.id === id) || null;
  },

  async create(saleData: SaleFormData): Promise<Sale> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newSale: Sale = {
      id: nextId++,
      customerName: saleData.customerName,
      customerEmail: saleData.customerEmail,
      products: saleData.products.map(p => ({
        id: p.productId,
        name: `Produto ${p.productId}`,
        quantity: p.quantity,
        price: p.price
      })),
      total: saleData.products.reduce((sum, p) => sum + (p.quantity * p.price), 0),
      status: 'pending',
      date: new Date().toISOString(),
      paymentMethod: saleData.paymentMethod
    };
    
    salesData.push(newSale);
    return newSale;
  },

  async update(id: number, saleData: Partial<SaleFormData>): Promise<Sale> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const index = salesData.findIndex(sale => sale.id === id);
    if (index === -1) {
      throw new Error('Venda não encontrada');
    }
    
    const updatedSale = {
      ...salesData[index],
      ...saleData,
      products: saleData.products ? saleData.products.map(p => ({
        id: p.productId,
        name: `Produto ${p.productId}`,
        quantity: p.quantity,
        price: p.price
      })) : salesData[index].products,
      total: saleData.products ? 
        saleData.products.reduce((sum, p) => sum + (p.quantity * p.price), 0) : 
        salesData[index].total
    };
    
    salesData[index] = updatedSale;
    return updatedSale;
  },

  async delete(id: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const index = salesData.findIndex(sale => sale.id === id);
    if (index === -1) {
      throw new Error('Venda não encontrada');
    }
    
    salesData.splice(index, 1);
  },

  async updateStatus(id: number, status: 'pending' | 'completed' | 'cancelled'): Promise<Sale> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const index = salesData.findIndex(sale => sale.id === id);
    if (index === -1) {
      throw new Error('Venda não encontrada');
    }
    
    salesData[index].status = status;
    return salesData[index];
  },

  async getSalesStats(): Promise<{
    totalSales: number;
    completedSales: number;
    pendingSales: number;
    cancelledSales: number;
    totalRevenue: number;
  }> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const completedSales = salesData.filter(sale => sale.status === 'completed');
    const pendingSales = salesData.filter(sale => sale.status === 'pending');
    const cancelledSales = salesData.filter(sale => sale.status === 'cancelled');
    const totalRevenue = completedSales.reduce((sum, sale) => sum + sale.total, 0);
    
    return {
      totalSales: salesData.length,
      completedSales: completedSales.length,
      pendingSales: pendingSales.length,
      cancelledSales: cancelledSales.length,
      totalRevenue
    };
  }
};

export default salesService;